import { type ReactNode } from 'react';

export interface EmptyStateProps {
  readonly icon?: ReactNode;
  readonly title: string;
  readonly description?: string;
  /** Optional CTA. Mirrors HTHD's `action: { label, onClick }` shape. */
  readonly action?: { readonly label: string; readonly onClick: () => void };
  readonly className?: string;
}

/**
 * Empty-data placeholder — used inside DataTable, list pages, and dashboards
 * when there's no content to render. Mirrors HTHD admin's `EmptyState.tsx`
 * so calls drop in unchanged.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className = '',
}: EmptyStateProps): JSX.Element {
  const cls = `ferro-empty-state${className ? ` ${className}` : ''}`;
  return (
    <div className={cls}>
      <div className="ferro-empty-state__icon" aria-hidden="true">
        {icon ?? <DefaultIcon />}
      </div>
      <p className="ferro-empty-state__title">{title}</p>
      {description ? <p className="ferro-empty-state__description">{description}</p> : null}
      {action ? (
        <button
          type="button"
          className="ferro-btn ferro-btn--primary ferro-btn--sm"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  );
}

function DefaultIcon(): JSX.Element {
  return (
    <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );
}
